import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { createFormConfig, updateFormConfig } from "../../services/FormService";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "tel", label: "Điện thoại" },
  { value: "textarea", label: "Textarea" },
  { value: "select", label: "Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio" },
  { value: "date", label: "Date" },
  { value: "file", label: "File" },
];

const FormConfigForm = ({ formConfig, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isActive: true, // Mặc định enable form
    fields: [],
    settings: {
      allowMultipleSubmissions: false,
      requireLogin: false,
      successMessage: "Cảm ơn bạn đã gửi thông tin!",
      submitButtonText: "Gửi thông tin",
    },
    documentUrls: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (formConfig) {
      setFormData({
        name: formConfig.name || "",
        description: formConfig.description || "",
        isActive: formConfig.isActive || false,
        fields: formConfig.fields || [],
        settings: {
          allowMultipleSubmissions:
            formConfig.settings?.allowMultipleSubmissions || false,
          requireLogin: formConfig.settings?.requireLogin || false,
          successMessage:
            formConfig.settings?.successMessage ||
            "Cảm ơn bạn đã gửi thông tin!",
          submitButtonText:
            formConfig.settings?.submitButtonText || "Gửi thông tin",
        },
        documentUrls: formConfig.documentUrls || [],
      });
    }
  }, [formConfig]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSettingsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
  };

  const addDocumentUrl = () => {
    const newDocumentUrl = {
      title: "",
      url: "",
      description: "",
      order: formData.documentUrls.length,
    };

    setFormData((prev) => ({
      ...prev,
      documentUrls: [...prev.documentUrls, newDocumentUrl],
    }));
  };

  const updateDocumentUrl = (index, field, value) => {
    setFormData((prev) => {
      const newDocumentUrls = [...prev.documentUrls];
      newDocumentUrls[index] = {
        ...newDocumentUrls[index],
        [field]: value,
      };
      return {
        ...prev,
        documentUrls: newDocumentUrls,
      };
    });
  };

  const removeDocumentUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      documentUrls: prev.documentUrls.filter((_, i) => i !== index),
    }));
  };

  const moveDocumentUrl = (index, direction) => {
    setFormData((prev) => {
      const newDocumentUrls = [...prev.documentUrls];
      const targetIndex = direction === "up" ? index - 1 : index + 1;

      if (targetIndex >= 0 && targetIndex < newDocumentUrls.length) {
        [newDocumentUrls[index], newDocumentUrls[targetIndex]] = [
          newDocumentUrls[targetIndex],
          newDocumentUrls[index],
        ];

        // Update order values
        newDocumentUrls[index].order = index;
        newDocumentUrls[targetIndex].order = targetIndex;
      }

      return {
        ...prev,
        documentUrls: newDocumentUrls,
      };
    });
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      type: "text",
      label: "",
      placeholder: "",
      required: false,
      options: [],
      validation: {},
      order: formData.fields.length,
      description: "",
      defaultValue: "",
    };

    setFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
  };

  const updateField = (index, field, value) => {
    setFormData((prev) => {
      const newFields = [...prev.fields];
      newFields[index] = {
        ...newFields[index],
        [field]: value,
      };
      return {
        ...prev,
        fields: newFields,
      };
    });
  };

  const updateFieldValidation = (index, validationField, value) => {
    setFormData((prev) => {
      const newFields = [...prev.fields];
      newFields[index] = {
        ...newFields[index],
        validation: {
          ...newFields[index].validation,
          [validationField]: value,
        },
      };
      return {
        ...prev,
        fields: newFields,
      };
    });
  };

  const addFieldOption = (fieldIndex) => {
    setFormData((prev) => {
      const newFields = [...prev.fields];
      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        options: [...newFields[fieldIndex].options, { value: "", label: "" }],
      };
      return {
        ...prev,
        fields: newFields,
      };
    });
  };

  const updateFieldOption = (fieldIndex, optionIndex, field, value) => {
    setFormData((prev) => {
      const newFields = [...prev.fields];
      const newOptions = [...newFields[fieldIndex].options];
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        [field]: value,
      };
      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        options: newOptions,
      };
      return {
        ...prev,
        fields: newFields,
      };
    });
  };

  const removeFieldOption = (fieldIndex, optionIndex) => {
    setFormData((prev) => {
      const newFields = [...prev.fields];
      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        options: newFields[fieldIndex].options.filter(
          (_, i) => i !== optionIndex
        ),
      };
      return {
        ...prev,
        fields: newFields,
      };
    });
  };

  const removeField = (index) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields
        .filter((_, i) => i !== index)
        .map((field, i) => ({
          ...field,
          order: i,
        })),
    }));
  };

  const moveField = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === formData.fields.length - 1)
    ) {
      return;
    }

    const newFields = [...formData.fields];
    const newIndex = direction === "up" ? index - 1 : index + 1;

    [newFields[index], newFields[newIndex]] = [
      newFields[newIndex],
      newFields[index],
    ];

    // Update order
    newFields.forEach((field, i) => {
      field.order = i;
    });

    setFormData((prev) => ({
      ...prev,
      fields: newFields,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Tên form là bắt buộc";
    }

    if (formData.fields.length === 0) {
      newErrors.fields = "Phải có ít nhất 1 field";
    }

    // Validate fields
    formData.fields.forEach((field, index) => {
      if (!field.label.trim()) {
        newErrors[`field_${index}_label`] = "Label là bắt buộc";
      }
      if (!field.id.trim()) {
        newErrors[`field_${index}_id`] = "ID là bắt buộc";
      }
      // Check for duplicate IDs
      const duplicateIds = formData.fields.filter((f) => f.id === field.id);
      if (duplicateIds.length > 1) {
        newErrors[`field_${index}_id`] = "ID phải là duy nhất";
      }
      // Validate options for select/radio/checkbox
      if (
        ["select", "radio", "checkbox"].includes(field.type) &&
        field.options.length === 0
      ) {
        newErrors[`field_${index}_options`] = "Phải có ít nhất 1 option";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Vui lòng kiểm tra lại thông tin");
      return;
    }

    try {
      setLoading(true);

      // Clean up field IDs
      const cleanedFields = formData.fields.map((field) => ({
        ...field,
        id: field.id.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase(),
      }));

      const submitData = {
        ...formData,
        fields: cleanedFields,
      };

      let response;
      if (formConfig) {
        response = await updateFormConfig(formConfig._id, submitData);
      } else {
        response = await createFormConfig(submitData);
      }

      if (response.success) {
        toast.success(
          formConfig ? "Cập nhật form thành công" : "Tạo form thành công"
        );
        onSave();
      } else {
        toast.error(response.message || "Có lỗi xảy ra");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("Lỗi khi lưu form");
    } finally {
      setLoading(false);
    }
  };

  const renderFieldOptions = (field, fieldIndex) => {
    if (!["select", "radio", "checkbox"].includes(field.type)) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Options
        </Typography>
        {field.options.map((option, optionIndex) => (
          <Box
            key={optionIndex}
            display="flex"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <TextField
              label="Value"
              value={option.value}
              onChange={(e) =>
                updateFieldOption(
                  fieldIndex,
                  optionIndex,
                  "value",
                  e.target.value
                )
              }
              size="small"
              sx={{ mr: 1, flex: 1 }}
            />
            <TextField
              label="Label"
              value={option.label}
              onChange={(e) =>
                updateFieldOption(
                  fieldIndex,
                  optionIndex,
                  "label",
                  e.target.value
                )
              }
              size="small"
              sx={{ mr: 1, flex: 1 }}
            />
            <IconButton
              onClick={() => removeFieldOption(fieldIndex, optionIndex)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          onClick={() => addFieldOption(fieldIndex)}
          size="small"
          startIcon={<AddIcon />}
        >
          Thêm Option
        </Button>
        {errors[`field_${fieldIndex}_options`] && (
          <Typography color="error" variant="caption" display="block">
            {errors[`field_${fieldIndex}_options`]}
          </Typography>
        )}
      </Box>
    );
  };

  const renderValidationFields = (field, fieldIndex) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Validation
        </Typography>
        <Grid container spacing={2}>
          {field.type === "text" || field.type === "textarea" ? (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Min Length"
                  type="number"
                  value={field.validation?.minLength || ""}
                  onChange={(e) =>
                    updateFieldValidation(
                      fieldIndex,
                      "minLength",
                      parseInt(e.target.value) || undefined
                    )
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max Length"
                  type="number"
                  value={field.validation?.maxLength || ""}
                  onChange={(e) =>
                    updateFieldValidation(
                      fieldIndex,
                      "maxLength",
                      parseInt(e.target.value) || undefined
                    )
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
            </>
          ) : null}

          {field.type === "number" ? (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Min Value"
                  type="number"
                  value={field.validation?.min || ""}
                  onChange={(e) =>
                    updateFieldValidation(
                      fieldIndex,
                      "min",
                      parseFloat(e.target.value) || undefined
                    )
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max Value"
                  type="number"
                  value={field.validation?.max || ""}
                  onChange={(e) =>
                    updateFieldValidation(
                      fieldIndex,
                      "max",
                      parseFloat(e.target.value) || undefined
                    )
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <TextField
              label="Pattern (Regex)"
              value={field.validation?.pattern || ""}
              onChange={(e) =>
                updateFieldValidation(fieldIndex, "pattern", e.target.value)
              }
              size="small"
              fullWidth
              placeholder="Ví dụ: ^[0-9]{10}$ cho số điện thoại"
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Basic Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Thông tin cơ bản
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tên Form"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleInputChange("isActive", e.target.checked)
                  }
                />
              }
              label="Kích hoạt form"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Mô tả"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Cài đặt Form
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.settings.allowMultipleSubmissions}
                  onChange={(e) =>
                    handleSettingsChange(
                      "allowMultipleSubmissions",
                      e.target.checked
                    )
                  }
                />
              }
              label="Cho phép gửi nhiều lần"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.settings.requireLogin}
                  onChange={(e) =>
                    handleSettingsChange("requireLogin", e.target.checked)
                  }
                />
              }
              label="Yêu cầu đăng nhập"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Thông báo thành công"
              value={formData.settings.successMessage}
              onChange={(e) =>
                handleSettingsChange("successMessage", e.target.value)
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Text nút gửi"
              value={formData.settings.submitButtonText}
              onChange={(e) =>
                handleSettingsChange("submitButtonText", e.target.value)
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Document URLs */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">
            Tài liệu / URLs ({formData.documentUrls.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addDocumentUrl}
          >
            Thêm URL
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Những URLs này sẽ hiển thị cho user sau khi họ gửi form thành công
        </Typography>

        {formData.documentUrls.map((docUrl, index) => (
          <Accordion key={index} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                <DragIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography sx={{ flex: 1 }}>
                  {docUrl.title || `URL ${index + 1}`}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveDocumentUrl(index, "up");
                    }}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveDocumentUrl(index, "down");
                    }}
                    disabled={index === formData.documentUrls.length - 1}
                  >
                    ↓
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDocumentUrl(index);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Tiêu đề tài liệu"
                    value={docUrl.title}
                    onChange={(e) =>
                      updateDocumentUrl(index, "title", e.target.value)
                    }
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="URL"
                    value={docUrl.url}
                    onChange={(e) =>
                      updateDocumentUrl(index, "url", e.target.value)
                    }
                    fullWidth
                    size="small"
                    required
                    placeholder="https://example.com/document.pdf"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Mô tả (tùy chọn)"
                    value={docUrl.description}
                    onChange={(e) =>
                      updateDocumentUrl(index, "description", e.target.value)
                    }
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Mô tả ngắn về tài liệu này..."
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Fields */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">
            Fields ({formData.fields.length})
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addField}>
            Thêm Field
          </Button>
        </Box>

        {errors.fields && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.fields}
          </Alert>
        )}

        {formData.fields.map((field, index) => (
          <Accordion key={field.id || index} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                <DragIcon sx={{ mr: 1, color: "text.secondary" }} />
                <Typography sx={{ flex: 1 }}>
                  {field.label || `Field ${index + 1}`} ({field.type})
                  {field.required && <span style={{ color: "red" }}> *</span>}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveField(index, "up");
                    }}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      moveField(index, "down");
                    }}
                    disabled={index === formData.fields.length - 1}
                  >
                    ↓
                  </Button>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      removeField(index);
                    }}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="ID"
                    value={field.id}
                    onChange={(e) => updateField(index, "id", e.target.value)}
                    fullWidth
                    size="small"
                    required
                    error={!!errors[`field_${index}_id`]}
                    helperText={
                      errors[`field_${index}_id`] || "Chỉ sử dụng chữ, số và _"
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Label"
                    value={field.label}
                    onChange={(e) =>
                      updateField(index, "label", e.target.value)
                    }
                    fullWidth
                    size="small"
                    required
                    error={!!errors[`field_${index}_label`]}
                    helperText={errors[`field_${index}_label`]}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={field.type}
                      onChange={(e) =>
                        updateField(index, "type", e.target.value)
                      }
                      label="Type"
                    >
                      {FIELD_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Placeholder"
                    value={field.placeholder}
                    onChange={(e) =>
                      updateField(index, "placeholder", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Default Value"
                    value={field.defaultValue}
                    onChange={(e) =>
                      updateField(index, "defaultValue", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Mô tả"
                    value={field.description}
                    onChange={(e) =>
                      updateField(index, "description", e.target.value)
                    }
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.required}
                        onChange={(e) =>
                          updateField(index, "required", e.target.checked)
                        }
                      />
                    }
                    label="Bắt buộc"
                  />
                </Grid>

                <Grid item xs={12}>
                  {renderFieldOptions(field, index)}
                </Grid>

                <Grid item xs={12}>
                  {renderValidationFields(field, index)}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Actions */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} disabled={loading}>
          Hủy
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Đang lưu..." : formConfig ? "Cập nhật" : "Tạo Form"}
        </Button>
      </Box>
    </Box>
  );
};

export default FormConfigForm;
